import { useQuery, useMutation, useQueryClient } from 'react-query';
import { apiClient, endpoints } from '../api/client';
import toast from 'react-hot-toast';

export interface FieldDefinition {
  name: string;
  required: boolean;
  data_type: string;
  selectors: string[];
  selector_type: 'css' | 'xpath' | 'regex' | 'json_path';
  attribute?: string;
  source?: 'html' | 'url'; // Where to extract the field from (default: 'html')
  regex_pattern?: string;
  default_value?: string;
  min_length?: number;
  max_length?: number;
  validation_pattern?: string;
  last_tested?: string;
  success_rate: number;
  failure_count: number;
  ai_corrections: number;
}

export interface PageDefinition {
  page_type:
    | 'search_results'
    | 'product_listing'
    | 'category_page'
    | 'seller_page'
    | 'seller_profile';
  url_pattern: string;
  fields: Record<string, FieldDefinition>;
  next_page_selector?: string;
  pagination_pattern?: string;
  content_container: string;
  item_selector: string;
  last_tested?: string;
  success_rate: number;
  avg_response_time?: number;
  ai_corrections: number;
  last_ai_correction?: string;
}

export interface MarketplaceDefinition {
  id: string;
  marketplace: string;
  name: string;
  version: string;
  domain: string;
  is_active: boolean;
  // Optional linkage to catalog entities in Store service
  brand_id?: string | null;
  product_id?: string | null;
  overall_success_rate: number;
  last_tested?: string;
  ai_maintenance_level: string;
  created_at: string;
  updated_at: string;
  created_by?: string | null;
  updated_by?: string | null;
  being_worked_by?: string | null;
  being_worked_at?: string | null;
  config?: Record<string, any>;
  // Inheritance fields for locale-based definitions
  is_template?: boolean;
  parent_definition_id?: string | null;
  // Locale-specific metadata
  country?: string;
  locale?: string;
  currency?: string;
  /** 0–1 from crawl_results; null if no Collector task results yet */
  scrape_success_rate?: number | null;
  scrape_last_tested?: string | null;
  /** verified | needs_maintenance | untested — from latest Collector tasks */
  scrape_health?: string;
}

// Lightweight template reference for dropdown selections
export interface TemplateDefinition {
  id: string;
  name: string;
  domain: string;
  marketplace: string;
}

export const useMarketplaceDefinitions = () => {
  return useQuery<MarketplaceDefinition[]>(
    'marketplaceDefinitions',
    async () => {
      // Request maximum allowed page size to get all definitions in one call
      const response = await apiClient.get(endpoints.definitions, {
        params: {
          size: 500,
          page: 1,
        },
      });
      return response.data.items || [];
    },
    {
      staleTime: 60000,
      cacheTime: 300000,
      refetchInterval: 30000,
    }
  );
};

export const useMarketplaceDefinition = (id: string) => {
  return useQuery<MarketplaceDefinition>(
    ['marketplaceDefinition', id],
    async () => {
      const response = await apiClient.get(endpoints.definition(id));
      return response.data;
    },
    {
      enabled: !!id,
    }
  );
};

export const useTemplateDefinitions = () => {
  return useQuery<TemplateDefinition[]>(
    'templateDefinitions',
    async () => {
      const response = await apiClient.get(endpoints.definitionTemplates);
      return response.data.templates || [];
    },
    {
      staleTime: 60000, // Cache for 1 minute
    }
  );
};

export const useResolvedDefinition = (id: string) => {
  return useQuery<MarketplaceDefinition>(
    ['resolvedDefinition', id],
    async () => {
      const response = await apiClient.get(endpoints.definitionResolved(id));
      return response.data;
    },
    {
      enabled: !!id,
    }
  );
};

export const fetchResolvedDefinition = async (id: string): Promise<MarketplaceDefinition> => {
  const response = await apiClient.get(endpoints.definitionResolved(id));
  return response.data;
};

export const useResolvedDefinitions = (ids: string[]) => {
  const uniqueIds = [...new Set(ids.filter(Boolean))];
  const queryKey = uniqueIds.slice().sort().join(',');
  return useQuery<Record<string, MarketplaceDefinition>>(
    ['resolvedDefinitions', queryKey],
    async () => {
      const results = await Promise.all(uniqueIds.map(id => fetchResolvedDefinition(id)));
      const byId: Record<string, MarketplaceDefinition> = {};
      for (const def of results) {
        if (def?.id) byId[def.id] = def;
      }
      return byId;
    },
    {
      enabled: uniqueIds.length > 0,
      staleTime: 60_000,
    }
  );
};

export const useCreateMarketplaceDefinition = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (definition: {
      name: string;
      version: string;
      domain: string;
      is_active?: boolean;
      config: Record<string, any>;
      // Inheritance fields
      is_template?: boolean;
      parent_definition_id?: string | null;
      // Locale-specific metadata
      country?: string;
      locale?: string;
      currency?: string;
      // Login credentials (stored in config.login)
      login_username?: string;
      login_password?: string;
      updated_by?: string;
    }) => {
      const response = await apiClient.post(endpoints.definitions, definition);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('marketplaceDefinitions');
        queryClient.invalidateQueries('templateDefinitions');
        toast.success('Marketplace definition created successfully!');
      },
      onError: (error: any) => {
        toast.error(
          `Failed to create definition: ${error.response?.data?.message || error.message}`
        );
      },
    }
  );
};

export const useUpdateMarketplaceDefinition = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      id,
      definition,
    }: {
      id: string;
      definition: Partial<{
        name: string;
        version: string;
        domain: string;
        is_active: boolean;
        overall_success_rate: number;
        ai_maintenance_level: string;
        config: Record<string, any>;
        // Inheritance fields
        is_template: boolean;
        parent_definition_id: string | null;
        // Locale-specific metadata
        country: string;
        locale: string;
        currency: string;
        // Login credentials (stored in config.login)
        login_username?: string;
        login_password?: string;
        updated_by?: string;
      }>;
    }) => {
      const response = await apiClient.put(endpoints.definition(id), definition);
      return response.data;
    },
    {
      onSuccess: (_, { id: _id }) => {
        queryClient.invalidateQueries('marketplaceDefinitions');
        queryClient.invalidateQueries('templateDefinitions');
        queryClient.invalidateQueries(['marketplaceDefinition', _id]);
        queryClient.invalidateQueries(['resolvedDefinition', _id]);
        toast.success('Marketplace definition updated successfully!');
      },
      onError: (error: any) => {
        toast.error(
          `Failed to update definition: ${error.response?.data?.message || error.message}`
        );
      },
    }
  );
};

export const useSetMarketplaceDefinitionWorkStatus = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ id, workingBy }: { id: string; workingBy: string | null }) => {
      const response = await apiClient.put(endpoints.definitionWorkStatus(id), {
        working_by: workingBy,
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('marketplaceDefinitions');
      },
      onError: (error: any) => {
        const detail = error.response?.data?.detail;
        const message =
          typeof detail === 'object'
            ? detail.message
            : detail || error.response?.data?.message || error.message;
        toast.error(`Failed to update work status: ${message}`);
      },
    }
  );
};

export const useDeleteMarketplaceDefinition = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (id: string) => {
      const response = await apiClient.delete(endpoints.definition(id));
      return response.data;
    },
    {
      onSuccess: (_, _id) => {
        queryClient.invalidateQueries('marketplaceDefinitions');
        toast.success('Marketplace definition deleted successfully!');
      },
      onError: (error: any) => {
        toast.error(
          `Failed to delete definition: ${error.response?.data?.message || error.message}`
        );
      },
    }
  );
};

export const useValidateMarketplaceDefinition = () => {
  return useMutation(
    async ({ id, autoCorrect = false }: { id: string; autoCorrect?: boolean }) => {
      const response = await apiClient.post(endpoints.validateMarketplace(id), {
        auto_correct: autoCorrect,
      });
      return response.data;
    },
    {
      onError: (error: any) => {
        toast.error(`Validation failed: ${error.response?.data?.message || error.message}`);
      },
    }
  );
};
